import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { navigationMenuLayout, selectedMenuItem, setSelectedMenuItem } from "@/components/Menu/menuSlice";
import { clearParcelShape, currentSelectedSearchResult, getPointLayerSearchResultByFipsApn, setParcelShapeError, setSelectedSearchResult, setSelectParcelShape } from "@/components/Search/searchSlice";
import { quoteSqlString } from '@/lib/search-query';
import * as bufferOperator from "@arcgis/core/geometry/operators/bufferOperator";
import Point from "@arcgis/core/geometry/Point";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import FeatureLayerView from "@arcgis/core/views/layers/FeatureLayerView";
import MapView from "@arcgis/core/views/MapView";
import { useEffect, useMemo, useRef } from "react";

/** Tracks parcel highlight handles so they can be released together. */
export function useHighlights() {
    const handles = useRef<__esri.Handle[]>([]);
    return useMemo(() => ({
        add: (handle: __esri.Handle) => { handles.current.push(handle); },
        clear: () => {
            handles.current.forEach((handle) => handle.remove());
            handles.current = [];
        },
    }), []);
}

interface ParcelSelectionOptions {
    view: MapView | null;
    polygonLayerView: FeatureLayerView | null;
    polygonLayer: FeatureLayer | null;
    pointLayer: FeatureLayer | null;
    highlights: ReturnType<typeof useHighlights>;
    stopTracking: () => void;
    onNotice: (message: string | null) => void;
}

/**
 * Connects map clicks to parcel search, and shows the selected search result on the map:
 * highlights and zooms to a parcel, or goes to a geocoded location.
 */
export function useParcelSelection({ view, polygonLayerView, polygonLayer, pointLayer, highlights, stopTracking, onNotice }: ParcelSelectionOptions) {
    const dispatch = useAppDispatch();
    const selectedResult = useAppSelector(currentSelectedSearchResult);
    const layout = useAppSelector(navigationMenuLayout);
    const menuItem = useAppSelector(selectedMenuItem);

    // Tapping the map selects the parcel under the tap, or clears the current selection.
    useEffect(() => {
        if (!view || !polygonLayerView || !pointLayer || layout === 'drawing') return;
        let active = true;
        let hitRequest = 0;
        let selectionRequest: { abort: () => void } | undefined;
        const clickHandle = view.on('click', async (event) => {
            if (selectedResult) {
                dispatch(setSelectedSearchResult(null));
                return;
            }
            if (polygonLayerView.dataUpdating) {
                onNotice('The parcel data is still loading. Please try again in a moment.');
                return;
            }
            const requestId = ++hitRequest;
            selectionRequest?.abort();
            try {
                const response = await view.hitTest(event);
                if (!active || requestId !== hitRequest) return;
                const fipsApns = response.results.flatMap((result) => result.type === 'graphic' && result.layer?.id === polygonLayer?.id
                    ? [result.graphic.attributes.fips_apn as string] : []);
                if (fipsApns.length > 0) {
                    onNotice(null);
                    selectionRequest = dispatch(getPointLayerSearchResultByFipsApn({ fipsApn: fipsApns, pointLayer }));
                }
            } catch {
                if (active && requestId === hitRequest) onNotice('This parcel could not load. Please try again.');
            }
        });
        return () => {
            active = false;
            clickHandle.remove();
            selectionRequest?.abort();
        };
    }, [dispatch, layout, onNotice, pointLayer, polygonLayer, polygonLayerView, selectedResult, view]);

    // Show the selected result: highlight and frame a parcel, or go to a geocoded point.
    useEffect(() => {
        dispatch(clearParcelShape());
        highlights.clear();
        if (!selectedResult) {
            dispatch(setSelectedMenuItem('closed'));
            return;
        }
        if (!view || !polygonLayerView || !polygonLayer) return;
        const controller = new AbortController();
        let active = true;
        stopTracking();
        const showSelection = async () => {
            if (selectedResult.type === 'geocode') {
                const shape = selectedResult.shape;
                if (!shape || !Number.isFinite(shape.x) || !Number.isFinite(shape.y)) throw new Error('Location unavailable');
                await view.goTo({ target: new Point({ latitude: shape.y, longitude: shape.x }), zoom: 15 });
                return;
            }
            const fipsAPN = selectedResult.fipsAPN;
            if (!fipsAPN) return;
            const query = polygonLayer.createQuery();
            query.where = `fips_apn = ${quoteSqlString(fipsAPN)}`;
            query.returnGeometry = true;
            const result = await polygonLayer.queryFeatures(query, { signal: controller.signal });
            if (!active) return;
            const feature = result.features[0];
            if (!feature?.geometry) throw new Error('Parcel boundary unavailable');
            dispatch(setSelectParcelShape({ fipsAPN, shape: feature.geometry.toJSON() }));
            highlights.add(polygonLayerView.highlight(feature));
            await view.goTo(bufferOperator.execute(feature.geometry, 100));
        };
        void showSelection().catch((error: Error) => {
            if (!active || error.name === 'AbortError') return;
            if (selectedResult.fipsAPN) {
                dispatch(setParcelShapeError({ fipsAPN: selectedResult.fipsAPN, message: 'The parcel boundary could not load. Select the parcel again to retry.' }));
            } else {
                onNotice('This location could not load. Please try again.');
            }
        });
        return () => {
            active = false;
            controller.abort();
            highlights.clear();
        };
    }, [dispatch, highlights, onNotice, polygonLayer, polygonLayerView, selectedResult, stopTracking, view]);

    // Leaving the Explore tab drops the highlight.
    useEffect(() => {
        if (menuItem !== 'explore') highlights.clear();
    }, [highlights, menuItem]);
}
